/**
 * Infinite scroll for collection pages.
 * Uses Shopify Section Rendering API to fetch next pages
 * and appends product grid cells to the existing grid.
 * Falls back to traditional pagination on error or no-JS.
 *
 * Reinitializes automatically after Prestige filter/sort reloads
 * (which replace .CollectionInner__Products via innerHTML).
 */
(function () {
	'use strict';

	let observer = null;

	function init() {
		// Clean up any previous observer
		if (observer) {
			observer.disconnect();
			observer = null;
		}

		const container = document.querySelector('[data-infinite-scroll]');
		if (!container) return;

		const grid = container.querySelector('.ProductList--grid');
		const sentinel = container.querySelector('[data-infinite-scroll-sentinel]');
		const pagination = container.querySelector('.Pagination');

		if (!grid || !sentinel) return;

		const sectionId = container.dataset.sectionId;
		const totalPages = parseInt(container.dataset.totalPages, 10);
		let currentPage = parseInt(container.dataset.currentPage, 10) || 1;
		let loading = false;

		// Already on last page — nothing to load
		if (currentPage >= totalPages) {
			sentinel.style.display = 'none';
			return;
		}

		// Strip any ?page= param from the URL so refresh always starts from page 1
		const cleanUrl = new URL(window.location.href);
		if (cleanUrl.searchParams.has('page')) {
			cleanUrl.searchParams.delete('page');
			history.replaceState(null, '', cleanUrl.toString());
		}

		// Hide traditional pagination (stays in DOM as no-JS fallback)
		if (pagination) pagination.style.display = 'none';

		// Remove bottom margin class — not needed with infinite scroll
		grid.classList.remove('ProductList--removeMargin');

		observer = new IntersectionObserver(
			function (entries) {
				if (entries[0].isIntersecting && !loading && currentPage < totalPages) {
					loadNextPage();
				}
			},
			{ rootMargin: '400px' },
		);

		observer.observe(sentinel);

		function loadNextPage() {
			loading = true;
			const nextPage = currentPage + 1;
			sentinel.classList.add('is-loading');

			// Preserve existing query params (filters, sort) and use Section Rendering API
			const url = new URL(window.location.href);
			url.searchParams.set('page', nextPage);
			url.searchParams.set('section_id', sectionId);

			fetch(url.toString())
				.then(function (res) {
					if (!res.ok) throw new Error('HTTP ' + res.status);
					return res.text();
				})
				.then(function (html) {
					const doc = new DOMParser().parseFromString(html, 'text/html');
					const newCells = doc.querySelectorAll('.ProductList--grid > .Grid__Cell');

					const fragment = document.createDocumentFragment();
					newCells.forEach(function (cell) {
						const imported = document.importNode(cell, true);
						// Force visibility — Prestige theme hides products by default
						// and reveals them via its own animation observer on page load.
						// Dynamically appended products miss that, so make them visible.
						const product = imported.querySelector('.ProductItem');
						if (product) {
							product.style.visibility = 'inherit';
							product.style.opacity = '1';
							product.style.transform = 'matrix(1, 0, 0, 1, 0, 0)';
						}
						fragment.appendChild(imported);
					});
					grid.appendChild(fragment);

					currentPage = nextPage;

					// Done loading all pages
					if (currentPage >= totalPages) {
						sentinel.style.display = 'none';
						observer.disconnect();
						observer = null;
					}

					sentinel.classList.remove('is-loading');
					loading = false;
				})
				.catch(function (err) {
					console.error('Infinite scroll error:', err);
					sentinel.classList.remove('is-loading');
					sentinel.style.display = 'none';
					// Restore traditional pagination as fallback
					if (pagination) pagination.style.display = '';
					observer.disconnect();
					observer = null;
					loading = false;
				});
		}
	}

	// Initial setup
	init();

	// Prestige's CollectionSection._reloadProducts() replaces .CollectionInner__Products
	// via innerHTML when filters or sort change, destroying the DOM nodes we captured.
	// It dispatches theme:loading:end when done — reinitialize to bind fresh nodes.
	document.addEventListener('theme:loading:end', init);
})();
